import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1535398670727-230dbc29b507?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxneW0lMjBmaXRuZXNzJTIwbW9kZWwlMjBoZXJvfGVufDB8fHx8MTc5MjkyNDkzN3ww&ixlib=rb-4.1.0&q=85";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[620px] overflow-hidden">
      <motion.div style={{ y: imgY }} className="absolute inset-0 scale-110">
        <img src={HERO_IMG} alt="Athlete training in DAFITEXCHANGE gear" className="h-full w-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/40 to-[#0A0A0C]/60" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0C]/70 via-transparent to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 h-full mx-auto max-w-[1440px] px-5 md:px-10 flex flex-col justify-end pb-24 md:pb-28">
        <MaskedLine delay={0.15}>
          <span className="inline-block text-[10px] md:text-xs tracking-[0.35em] text-[#D3FF24] font-medium mb-5 border border-[#D3FF24]/40 px-3 py-1.5">
            DAFITEXCHANGE.COM — GYM &amp; FITNESS WEAR
          </span>
        </MaskedLine>

        <h1 className="font-display font-black leading-[0.9] tracking-tighter text-[13.5vw] sm:text-7xl lg:text-8xl xl:text-[7.5rem]">
          <MaskedLine delay={0.3}>ENGINEERED</MaskedLine>
          <MaskedLine delay={0.42}>
            FOR THE <span className="text-[#D3FF24]">RELENTLESS.</span>
          </MaskedLine>
        </h1>

        <MaskedLine delay={0.6} className="mt-6 max-w-md">
          <p className="text-sm md:text-base text-white/60 leading-relaxed">
            Performance gym wear for men and women. 47 pieces, zero compromises —
            built for the ones who show up when nobody is watching.
          </p>
        </MaskedLine>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.7 }}
          className="mt-9 flex flex-wrap gap-4"
        >
          <Link
            to="/shop?cat=men"
            data-testid="hero-shop-men"
            className="group inline-flex items-center gap-3 bg-[#D3FF24] text-[#0A0A0C] px-8 py-4 text-xs font-bold tracking-[0.25em] hover:bg-[#BEE61B] transition-colors duration-300"
          >
            SHOP MEN
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
          </Link>
          <Link
            to="/shop?cat=women"
            data-testid="hero-shop-women"
            className="group inline-flex items-center gap-3 border border-white/25 text-white px-8 py-4 text-xs font-bold tracking-[0.25em] hover:border-[#D3FF24] hover:text-[#D3FF24] transition-colors duration-300"
          >
            SHOP WOMEN
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-white/40"
      >
        <ChevronDown className="h-5 w-5 animate-bounce" />
      </motion.div>
    </section>
  );
}
